import {
  RadioTransport,
  SerialPortLike,
  NavigatorWithSerial,
} from './types';

const MAGIC_BYTE_1 = 0x94;
const MAGIC_BYTE_2 = 0xc3;

/**
 * WebSerial transport driver for Meshtastic devices over USB/UART.
 * Implements the Meshtastic 0x94 0xC3 framing protocol state machine.
 */
export class SerialTransport implements RadioTransport {
  readonly name = 'WebSerial';

  private port: SerialPortLike | null = null;
  private lastPort: SerialPortLike | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private isReading = false;

  private packetHandlers: ((data: Uint8Array) => void)[] = [];
  private errorHandlers: ((error: Error) => void)[] = [];
  private disconnectHandlers: (() => void)[] = [];

  // Framing parser state
  private buffer: number[] = [];

  /**
   * Requests a serial port from the user and opens communication at 115200 baud.
   */
  async connect(): Promise<void> {
    if (!('serial' in navigator)) {
      throw new Error('WebSerial API is not supported by this browser. Use Chrome, Edge, Brave or Opera.');
    }

    try {
      const nav = navigator as NavigatorWithSerial;
      if (!nav.serial) {
        throw new Error('WebSerial API is not supported by this browser.');
      }
      this.port = await nav.serial.requestPort();
      this.lastPort = this.port;
      await this.port.open({ baudRate: 115200 });

      if (!this.port.writable) {
        throw new Error('Serial port writable stream is not available.');
      }
      this.writer = this.port.writable.getWriter();
      this.isReading = true;

      // Write 4 wake/resync bytes (0x94) to reset radio MCU framing state machine
      try {
        await this.writer.write(new Uint8Array([MAGIC_BYTE_1, MAGIC_BYTE_1, MAGIC_BYTE_1, MAGIC_BYTE_1]));
      } catch (e) {
        console.warn('[SerialTransport] Could not send resync bytes:', e);
      }

      // Start non-blocking stream reader loop
      this.readLoop().catch((err) => {
        this.emitError(err instanceof Error ? err : new Error(String(err)));
      });

      // Handle spontaneous hardware unplug
      if (this.port.addEventListener) {
        this.port.addEventListener('disconnect', () => {
          this.handleDisconnect();
        });
      }
    } catch (err) {
      await this.disconnect();
      throw err;
    }
  }

  /**
   * Re-establishes serial communication with a previously authorized port without user prompt.
   *
   * Why: Enables automatic recovery after USB power fluctuations, cable jiggling, or MCU reboots
   * during vehicle operation. Reuses the approved SerialPort handle or retrieves granted ports
   * via navigator.serial.getPorts().
   */
  async reconnect(): Promise<void> {
    if (this.isConnected()) return;

    if (!('serial' in navigator)) {
      throw new Error('WebSerial API is not supported by this browser.');
    }

    const nav = navigator as NavigatorWithSerial;
    let targetPort: SerialPortLike | null = this.port || this.lastPort;

    if (!targetPort && typeof nav.serial?.getPorts === 'function') {
      try {
        const ports: SerialPortLike[] = await nav.serial.getPorts();
        if (ports && ports.length > 0) {
          targetPort = ports[0];
        }
      } catch (err) {
        console.warn('[SerialTransport] getPorts failed:', err);
      }
    }

    if (!targetPort) {
      throw new Error('No previously authorized serial port available for background reconnect.');
    }

    this.port = targetPort;
    this.lastPort = targetPort;

    try {
      await this.port.open({ baudRate: 115200 });

      if (!this.port.writable) {
        throw new Error('Serial port writable stream is not available.');
      }
      this.writer = this.port.writable.getWriter();
      this.isReading = true;

      // Write 4 wake/resync bytes (0x94) to reset radio MCU framing state machine
      try {
        await this.writer.write(new Uint8Array([MAGIC_BYTE_1, MAGIC_BYTE_1, MAGIC_BYTE_1, MAGIC_BYTE_1]));
      } catch (e) {
        console.warn('[SerialTransport] Could not send resync bytes:', e);
      }

      this.readLoop().catch((err) => {
        this.emitError(err instanceof Error ? err : new Error(String(err)));
      });

      if (this.port.addEventListener) {
        this.port.addEventListener('disconnect', () => {
          this.handleDisconnect();
        });
      }
      console.info('[SerialTransport] Reconnected successfully');
    } catch (err) {
      console.error('[SerialTransport] Reconnect failed:', err);
      await this.disconnect();
      throw err;
    }
  }

  /**
   * Safely closes stream reader, writer, and serial port.
   */
  async disconnect(): Promise<void> {
    this.isReading = false;

    if (this.reader) {
      try {
        await this.reader.cancel();
      } catch {
        // Reader might already be closed
      }
      try {
        this.reader.releaseLock();
      } catch {
        // Lock might already be released
      }
      this.reader = null;
    }

    if (this.writer) {
      try {
        this.writer.releaseLock();
      } catch {
        // Writer lock might already be released
      }
      this.writer = null;
    }

    if (this.port) {
      try {
        await this.port.close();
      } catch {
        // Port might already be closed
      }
      this.port = null;
    }

    this.buffer = [];
  }

  /**
   * Encapsulates raw ToRadio Protobuf bytes in the Meshtastic 0x94 0xC3 framing header and writes to serial.
   *
   * @param payload - Serialized Protobuf payload
   */
  async send(payload: Uint8Array): Promise<void> {
    if (!this.writer || !this.port) {
      throw new Error('Serial port is not connected.');
    }

    const len = payload.length;
    // Meshtastic framing requires 4-byte header: 0x94 0xC3 followed by 2-byte Big-Endian length
    const frame = new Uint8Array(4 + len);
    frame[0] = MAGIC_BYTE_1;
    frame[1] = MAGIC_BYTE_2;
    frame[2] = (len >> 8) & 0xff;
    frame[3] = len & 0xff;
    frame.set(payload, 4);

    await this.writer.write(frame);
  }

  onPacket(handler: (data: Uint8Array) => void): void {
    this.packetHandlers.push(handler);
  }

  onError(handler: (error: Error) => void): void {
    this.errorHandlers.push(handler);
  }

  onDisconnect(handler: () => void): void {
    this.disconnectHandlers.push(handler);
  }

  isConnected(): boolean {
    return this.port !== null && this.writer !== null && this.isReading;
  }

  /**
   * Continuous background read loop reading Uint8Array chunks from serial port.
   */
  private async readLoop(): Promise<void> {
    while (this.isReading && this.port && this.port.readable) {
      try {
        this.reader = this.port.readable.getReader();
        while (this.isReading) {
          const { value, done } = await this.reader.read();
          if (done) {
            console.debug('[SerialTransport] Read stream signalled done');
            break;
          }
          if (value && value.length > 0) {
            console.debug(`[SerialTransport] Received ${value.length} raw bytes from USB`);
            this.processBytes(value);
          }
        }
      } catch (err) {
        if (this.isReading) {
          this.emitError(err instanceof Error ? err : new Error(String(err)));
        }
        break;
      } finally {
        if (this.reader) {
          try {
            this.reader.releaseLock();
          } catch {
            // Reader lock cleanup
          }
          this.reader = null;
        }
      }
    }

    this.handleDisconnect();
  }

  /**
   * State-machine parser for Meshtastic framing protocol:
   * Header: 0x94 0xC3 [Length_MSB] [Length_LSB] [Length Bytes of Protobuf Data].
   *
   * @param chunk - Newly arrived byte buffer from USB serial stream
   */
  processBytes(chunk: Uint8Array): void {
    for (let i = 0; i < chunk.length; i++) {
      this.buffer.push(chunk[i]);
    }

    // Process all complete frames currently assembled in buffer
    while (this.buffer.length >= 4) {
      // Seek next 0x94 0xC3 sequence to re-align if out of sync
      const magicIndex = this.findMagicHeader();
      if (magicIndex === -1) {
        // Keep last byte in case it's 0x94 and next chunk has 0xC3
        const last = this.buffer[this.buffer.length - 1];
        this.buffer = last === MAGIC_BYTE_1 ? [MAGIC_BYTE_1] : [];
        return;
      }

      if (magicIndex > 0) {
        // Discard preamble noise preceding magic header
        this.buffer.splice(0, magicIndex);
      }

      if (this.buffer.length < 4) {
        // Wait for length bytes
        return;
      }

      // Read Big-Endian payload length
      const length = (this.buffer[2] << 8) | this.buffer[3];
      const totalFrameSize = 4 + length;

      if (this.buffer.length < totalFrameSize) {
        // Wait for remaining frame bytes to arrive
        return;
      }

      // Extract raw Protobuf payload without the 4-byte framing header
      const payloadBytes = new Uint8Array(this.buffer.slice(4, totalFrameSize));
      this.buffer.splice(0, totalFrameSize);

      console.debug(`[SerialTransport] Extracted frame: ${payloadBytes.length} byte protobuf payload`);

      // Deliver packet to subscribers
      for (const handler of this.packetHandlers) {
        try {
          handler(payloadBytes);
        } catch (handlerErr) {
          console.error('[SerialTransport] Error in packet handler:', handlerErr);
        }
      }
    }
  }

  private findMagicHeader(): number {
    for (let i = 0; i < this.buffer.length - 1; i++) {
      if (this.buffer[i] === MAGIC_BYTE_1 && this.buffer[i + 1] === MAGIC_BYTE_2) {
        return i;
      }
    }
    return -1;
  }

  private handleDisconnect(): void {
    const wasConnected = this.isConnected();
    this.isReading = false;
    this.port = null;
    this.writer = null;
    this.reader = null;
    this.buffer = [];

    if (wasConnected) {
      for (const handler of this.disconnectHandlers) {
        try {
          handler();
        } catch (err) {
          console.error('[SerialTransport] Disconnect handler error:', err);
        }
      }
    }
  }

  private emitError(error: Error): void {
    for (const handler of this.errorHandlers) {
      try {
        handler(error);
      } catch (err) {
        console.error('[SerialTransport] Error handler error:', err);
      }
    }
  }
}
